type VideoFormat = "landscape" | "short";

interface PlayerParts {
  readonly close: HTMLButtonElement;
  readonly frame: HTMLIFrameElement;
  readonly root: HTMLDivElement;
}

function findTrigger(target: EventTarget | null): HTMLAnchorElement | null {
  if (!(target instanceof Element)) return null;
  const trigger = target.closest<HTMLAnchorElement>("a.video-trigger");
  return trigger instanceof HTMLAnchorElement ? trigger : null;
}

function isUnmodifiedPrimaryClick(event: MouseEvent): boolean {
  return event.button === 0 && !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey;
}

function readFormat(trigger: HTMLAnchorElement): VideoFormat {
  return trigger.dataset["videoFormat"] === "short" ? "short" : "landscape";
}

function createPlayer(trigger: HTMLAnchorElement, videoId: string): PlayerParts {
  const format = readFormat(trigger);
  const heading = trigger.closest("article")?.querySelector("h3");
  const title = heading?.textContent.trim() ?? "Video";
  const root = document.createElement("div");
  const frame = document.createElement("iframe");
  const close = document.createElement("button");

  root.className = "player";
  root.dataset["format"] = format;
  frame.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?autoplay=1`;
  frame.title = title;
  frame.allow = "accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; web-share";
  frame.allowFullscreen = true;
  frame.referrerPolicy = "strict-origin-when-cross-origin";
  close.className = "player-close";
  close.type = "button";
  close.textContent = "Close video";
  root.append(frame, close);

  return { close, frame, root };
}

function activatePlayer(trigger: HTMLAnchorElement): void {
  const videoId = trigger.dataset["videoId"];
  const slot = trigger.nextElementSibling;
  if (videoId === undefined || !(slot instanceof HTMLDivElement)) return;

  const player = createPlayer(trigger, videoId);
  trigger.hidden = true;
  slot.replaceChildren(player.root);
  player.close.focus();
  player.close.addEventListener("click", () => {
    player.frame.src = "about:blank";
    slot.replaceChildren();
    trigger.hidden = false;
    trigger.focus();
  }, { once: true });
}

function handleClick(event: MouseEvent): void {
  const trigger = findTrigger(event.target);
  if (trigger === null || !isUnmodifiedPrimaryClick(event)) return;
  event.preventDefault();
  activatePlayer(trigger);
}

document.addEventListener("click", handleClick);
